import React, { ReactNode, useEffect, useMemo, useState } from "react";

// UX
import CardGenerateOutboundFreezeTimeUX from "./CardGenerateOutboundFreezeTimeUX";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";

// Utils
import { dateToStringBE } from "react-lib/utils/dateUtils";

// Interface
import { useIntl } from "react-intl";
import { 
  State,
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  GenerateOutboundDataType,
  TaskOptions,
  TableHeaderType,
} from "./sequence/GenerateOutboundFreezeTime";

// Serializer
import GenOutboundInterfaceLogSerializer from "issara-sdk/types/GenOutboundInterfaceLogSerializer_apps_INF";

type CardGenerateOutboundFreezeTimeProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence,
  GenerateOutboundFreezeTimeSequnce: State["GenerateOutboundFreezeTimeSequence"];
  languageUX: "en" | "th";
} & PickedProps;

const CardGenerateOutboundFreezeTime = (props: CardGenerateOutboundFreezeTimeProps) => {
  const intl = useIntl();

  // useState
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);

  // useEffect
  useEffect(() => {
    props.runSequence({ sequence: "GenerateOutboundFreezeTime", restart: true })
  }, []);

  useEffect(() => {
    if (props.errorMessage?.[CARD_NAME] || props.successMessage?.[CARD_NAME]) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data}</div>;
  };

  const handleChangeData = (_event: any, data: any) => {
    const name = data.name as keyof GenerateOutboundDataType;
    
    props.setProp(`GenerateOutboundFreezeTimeSequence.generateOutboundData.${name}`, data?.value);
  };

  const handleChangeDate = (date: string) => {
    props.setProp(`GenerateOutboundFreezeTimeSequence.generateOutboundData.freezeDate`, date);
  };

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`GenerateOutboundFreezeTimeSequence.paginatedItems.${key}`, value);

    props.runSequence({
      sequence: "GenerateOutboundFreezeTime",
      action: "FETCH",
    });
  };

  const handleGenerate = () => {
    props.runSequence({
      sequence: "GenerateOutboundFreezeTime",
      action: "GENERATE",
    });
  }

  const handleFetch = () => {
    props.runSequence({
      sequence: "GenerateOutboundFreezeTime",
      action: "FETCH",
    });
  };

  const handleDisabledGenerate = useMemo(() => {
    const freezeDate = props.GenerateOutboundFreezeTimeSequnce?.generateOutboundData?.freezeDate;
    const task = props.GenerateOutboundFreezeTimeSequnce?.generateOutboundData?.task;

    return !(freezeDate && task);
  }, [
    props.GenerateOutboundFreezeTimeSequnce?.generateOutboundData?.freezeDate,
    props.GenerateOutboundFreezeTimeSequnce?.generateOutboundData?.task,
  ]);

  // useMemo
  const outboundList = useMemo(() => {
    return (props.GenerateOutboundFreezeTimeSequnce?.outboundList || []).map(
      (item: GenOutboundInterfaceLogSerializer) => {
        return {
          ...item,
          ...tableHeaders.reduce(
            (data: Partial<Record<keyof GenOutboundInterfaceLogSerializer, ReactNode>>, header) => {
              const key = header.accessor;

              if (key === "created_at") {
                data[key] = columnTableCenter(dateToStringBE(item[key]));
              } else if (key === "error_message") {
                data[key] = item[key];
              } else {
                data[key] = columnTableCenter(item[key]);
              }

              return data;
            }, {}
          ),
        }
      }
    )
  }, [
    props.GenerateOutboundFreezeTimeSequnce?.outboundList
  ]);

  return (
    <>
      {openSnackMessage && (
        <SnackMessage
          // func
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_NAME}`, null);
            props.setProp(`successMessage.${CARD_NAME}`, null);
          }}
          error={props.errorMessage?.[CARD_NAME]}
          success={props.successMessage?.[CARD_NAME]}
          languageUX={props.languageUX}
        />
      )}

      <CardGenerateOutboundFreezeTimeUX
        onChangeData={handleChangeData}
        onChangeDate={handleChangeDate}
        generateOutboundData={props.GenerateOutboundFreezeTimeSequnce?.generateOutboundData}
        // dropdown
        taskOptions={TaskOptions || []}
        // table
        tableHeaders={tableHeaders}
        outboundList={outboundList}
        paginatedItems={props.GenerateOutboundFreezeTimeSequnce?.paginatedItems}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        generateButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleGenerate}
            disabled={handleDisabledGenerate}
            // data
            paramKey={`${CARD_NAME}_GENERATE`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_GENERATE`]}
            // config
            color="green"
            title="Generate"
            name="GENERATE"
            size="medium"
          />
        }
        refreshButton={
          <ButtonLoadCheck
            setProp={props.setProp}
            onClick={handleFetch}
            // data
            paramKey={`${CARD_NAME}_FETCH`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_FETCH`]}
            // config
            icon="refresh"
            color="orange"
            name="FETCH"
            size="medium"
          />
        }
      />
    </>
  );
};

CardGenerateOutboundFreezeTime.displayName = "CardGenerateOutboundFreezeTime";

export default React.memo(CardGenerateOutboundFreezeTime);

const tableHeaders: TableHeaderType<GenOutboundInterfaceLogSerializer>[] = [
  {
    Header: "Generate Date",
    accessor: "created_at",
    width: 150,
  },
  {
    Header: "Task",
    accessor: "task_label",
    width: 350,
  },
  {
    Header: "Status",
    accessor: "status_label",
    width: 120,
  },
  {
    Header: "Result",
    accessor: "result_label",
    width: 120,
  },
  {
    Header: "Error Message",
    accessor: "error_message",
  }
];